import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import LeadSaleService, {
  IRegisterLeadSaleParams,
} from '@clickersphere/core/services/LeadSaleService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { LeadSaleQueryData, LEAD_SALES_QUERY_KEY } from './useLeadSales';

export function useRegisterLeadSale(leadId: string) {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { isPending: registerSaleIsPending, mutateAsync: registerSaleAsync } =
    useMutation({
      mutationFn: async (data: IRegisterLeadSaleParams) => {
        const sale = await LeadSaleService.register(leadId, data);
        return sale as LeadSaleQueryData;
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEAD_SALES_QUERY_KEY, leadId]),
        });

        const tempId = String(crypto.randomUUID());
        const now = new Date().toISOString();

        queryClient.setQueryData<LeadSaleQueryData[]>(
          buildQueryKey([LEAD_SALES_QUERY_KEY, leadId]),
          (old) => [
            ...(old ?? []).concat({
              ...variables,
              id: tempId,
              tenantId: '',
              leadId,
              items: [],
              installments: [],
              createdAt: now,
              updatedAt: now,
              queryStatus: 'pending',
            }),
          ],
        );

        return { tempId };
      },
      onSuccess: async (data, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEAD_SALES_QUERY_KEY, leadId]),
        });

        queryClient.setQueryData<LeadSaleQueryData[]>(
          buildQueryKey([LEAD_SALES_QUERY_KEY, leadId]),
          (old) =>
            old?.map((item) => (item.id === context.tempId ? data : item)),
        );

        toast(t('catalog.toasts.saleRegistered'), 'success');
      },
      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEAD_SALES_QUERY_KEY, leadId]),
        });

        queryClient.setQueryData<LeadSaleQueryData[]>(
          buildQueryKey([LEAD_SALES_QUERY_KEY, leadId]),
          (old) => old?.filter((item) => item.id !== context?.tempId),
        );

        toast(t('catalog.toasts.saleRegisterError'), 'error');
      },
    });

  return {
    registerSaleAsync,
    registerSaleIsPending,
  };
}
